/**
 * OuCalendarService
 *
 * 회사캘린더(조직 공용 계정)의 OU(조직 단위) 목록을 서버에서 조회해 메모리에 캐시한다.
 * 로그인(계정 로드) 완료 시 user saga가 async 프리페치하며(LOGOUT 시 reset),
 * 성공/실패 결과를 세션 내 캐시하여 재요청 스팸을 방지한다.
 *
 * 서버가 role에 따라 403을 반환하거나 목록에 없는 OU 코드가 있을 수 있으므로,
 * 매칭 실패·에러 시 호출부는 기존 고정 라벨로 조용히 fallback해야 한다.
 */

import RestClient from 'services/restClient'
import { getReduxStore } from 'env'

export interface OuListItem {
  ou: string
  ou_name: string | null
}

/** 회사 전체(워크스페이스 전체)를 가리키는 특수 OU 코드. 이름은 워크스페이스명으로 표시한다. */
export const COMPANY_OU_CODE = 'd3f8lt'

/** getReduxStore()가 반환하는 객체 중 이 서비스가 필요한 최소 구조. */
interface ReduxStoreLike {
  getState: () => {
    user?: {
      workspace?: {
        workspaceName?: string
      } | null
    }
  }
}

interface OuListResponse {
  code?: number
  data?: {
    total?: number
    ou_list?: OuListItem[]
  }
}

let restClient: RestClient | null = null
// key: 소문자 OU 코드, value: ou_name (null = 서버가 이름 미프로비저닝 → 워크스페이스명으로 표시)
let ouNameByCode: Map<string, string | null> | null = null // null = 아직 로드 전
let loadPromise: Promise<void> | null = null
const listeners = new Set<() => void>()

/**
 * restClient 인스턴스를 주입한다.
 * 앱 초기화 시 myNet.ts의 restClient를 전달해야 한다.
 */
const init = (client: RestClient): void => {
  restClient = client
}

/**
 * 로드 완료(성공/실패 무관) 시 등록된 리스너를 순회 호출한다.
 * 리스너 콜백에서 발생한 에러는 catch하여 다른 리스너 호출을 막지 않는다.
 */
const notifyListeners = (): void => {
  for (const listener of listeners) {
    try {
      listener()
    } catch (error) {
      console.error('OuCalendarService listener error:', error)
    }
  }
}

/**
 * OU 목록을 최초 1회만 실제로 fetch한다.
 * in-flight promise를 공유하여 중복 호출을 방지하고, 이미 로드됐으면 즉시 resolve한다.
 * 실패(403 포함)해도 throw하지 않고 빈 Map으로 캐시 처리하여 재시도 스팸을 방지한다.
 */
const load = (): Promise<void> => {
  if (ouNameByCode != null) return Promise.resolve()
  if (loadPromise != null) return loadPromise

  loadPromise = (async () => {
    try {
      if (restClient == null) {
        throw new Error('OuCalendarService: restClient not initialized. Call init() first.')
      }
      const response = await restClient.get<OuListResponse>('/ou_calendar/ous')
      const ouList = response.data?.data?.ou_list ?? []
      const map = new Map<string, string | null>()
      for (const item of ouList) {
        if (item?.ou) {
          // ou_name이 null/빈 값인 미프로비저닝 OU도 항목은 유지한다 (null로 정규화).
          map.set(item.ou.toLowerCase(), item.ou_name || null)
        }
      }
      ouNameByCode = map
    } catch (error) {
      console.warn('OuCalendarService: OU 목록 로드 실패, fallback 라벨을 사용합니다.', error)
      ouNameByCode = new Map()
    } finally {
      notifyListeners()
    }
  })()

  return loadPromise
}

/**
 * 앱 초기화 전에는 getReduxStore()가 throw할 수 있으므로 안전하게 처리한다.
 * 워크스페이스명이 없으면 undefined.
 */
const getWorkspaceName = (): string | undefined => {
  try {
    const store = getReduxStore() as ReduxStoreLike | null
    return store?.getState()?.user?.workspace?.workspaceName ?? undefined
  } catch {
    return undefined
  }
}

/**
 * OU 코드에 해당하는 표시 이름을 반환한다 (대소문자 무시).
 * - COMPANY_OU_CODE(회사 전체)는 OU 목록 로드 여부와 무관하게 워크스페이스명을 우선 반환하고,
 *   없으면 OU 목록 캐시 → undefined 순으로 fallback한다.
 * - 목록에 있지만 ou_name이 null/빈 값인 OU(미프로비저닝)도 워크스페이스명으로 표시한다.
 * - 아직 로드되지 않았거나 목록에 아예 없는 코드는 undefined.
 */
const getOuName = (ouCode: string): string | undefined => {
  const normalizedCode = ouCode.toLowerCase()

  if (normalizedCode === COMPANY_OU_CODE) {
    const workspaceName = getWorkspaceName()
    if (workspaceName) return workspaceName
  }

  if (ouNameByCode == null || !ouNameByCode.has(normalizedCode)) return undefined

  const ouName = ouNameByCode.get(normalizedCode)
  if (ouName) return ouName

  // 목록에는 있으나 이름이 없는 OU → 워크스페이스명 fallback (없으면 undefined)
  return getWorkspaceName()
}

/** OU 목록이 로드 완료(성공/실패 무관)됐는지 여부. */
const isLoaded = (): boolean => ouNameByCode != null

/**
 * 로드 완료(성공/실패 무관) 시 호출되는 리스너를 등록한다.
 * 반환값은 해제 함수.
 */
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * 캐시/in-flight 상태 및 리스너를 초기화한다.
 * LOGOUT 시 다른 계정으로 재로그인할 때 이전 계정의 OU 캐시가 남지 않도록 호출한다.
 */
const reset = (): void => {
  ouNameByCode = null
  loadPromise = null
  listeners.clear()
}

const ouCalendarService = { init, load, getOuName, isLoaded, subscribe, reset }

export default ouCalendarService
